import { type JSX, useMemo } from "react";

import { tenantConfig } from "@tenant-config";
import { EmailTextButton } from "@vega-core/components/Buttons/EmailTextButton/EmailTextButton";
import { LinkTextButton } from "@vega-core/components/Buttons/LinkTextButton";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";

import { ModalRoutes } from "./constants";

/** Same shape as tenant config settingsHelpFaq body; defined here to avoid importing types from @tenant-config (circular dependency). */
type SettingsHelpFaqBodySegment =
  | { type: "email" }
  | { type: "permissionsLink" }
  | { type: "text"; value: string };

function renderBodySegments(
  body: SettingsHelpFaqBodySegment[],
  helpEmail: string,
  navigate: (route: string) => void,
): JSX.Element {
  return (
    <>
      {body.map((segment, i) => {
        const key =
          segment.type === "text"
            ? `text-${i}-${segment.value.slice(0, 24)}`
            : `${segment.type}-${i}`;

        if (segment.type === "text") {
          return <span key={key}>{segment.value}</span>;
        }

        if (segment.type === "email") {
          return <EmailTextButton email={helpEmail} key={key} />;
        }

        return (
          <LinkTextButton
            className="h-auto"
            key={key}
            onClick={() => navigate(ModalRoutes.Permissions)}
            text={m.userManagement_help_permissionsTable()}
          />
        );
      })}
    </>
  );
}

/**
 * Fallback when tenant does not define content.settingsHelpFaq (e.g. ares, carlyle).
 * Uses translation keys and interactive elements (email link, permissions link).
 */
const getFallbackFaqItems = (
  navigate: (route: string) => void,
  helpEmail: string,
  companyName: string,
) => [
  {
    title: m.userManagement_help_faq_updateProfile_title(),
    body: (
      <>
        {m.settings_home_faq_1({ companyName })}
        <EmailTextButton email={helpEmail} />.
      </>
    ),
  },
  {
    title: m.userManagement_help_faq_changePermissions_title(),
    body: (
      <>
        {m.settings_home_faq_2()}{" "}
        <LinkTextButton
          onClick={() => navigate(ModalRoutes.Permissions)}
          text={m.userManagement_help_permissionsTable()}
        />
        .
      </>
    ),
  },
  {
    title: m.userManagement_help_faq_noInvitation_title(),
    body: (
      <>
        {m.settings_home_faq_3()} <EmailTextButton email={helpEmail} />.
      </>
    ),
  },
  {
    title: m.userManagement_help_faq_cannotChangeRoles_title(),
    body: (
      <>
        {m.settings_home_faq_4()}{" "}
        <LinkTextButton
          className="h-auto"
          onClick={() => navigate(ModalRoutes.Permissions)}
          text={m.userManagement_help_permissionsTable()}
        />
        .
      </>
    ),
  },
  {
    title: m.userManagement_help_faq_changeEmail_title(),
    body: (
      <>
        {m.settings_home_faq_5_0()} <EmailTextButton email={helpEmail} />
        {m.settings_home_faq_5_1()}
      </>
    ),
  },
  {
    title: m.userManagement_help_faq_changePassword_title(),
    body: m.userManagement_help_faq_changePassword_body(),
  },
];

/**
 * Returns Settings Permissions Help FAQ from tenant config.
 * Build-time: config defines structure (segments); runtime only injects email/link components.
 * Fallback: when not in config, use translation keys and interactive elements (6 items).
 */
export const useFaqItems = (navigate: (route: string) => void) => {
  const {
    contacts,
    global: { companyName },
  } = usePublicConfig();

  return useMemo(() => {
    const tenantFaq = tenantConfig.content.settingsHelpFaq;

    if (tenantFaq?.length) {
      return tenantFaq.map((item) => ({
        title: item.title,
        body: renderBodySegments(item.body, contacts.helpEmail, navigate),
      }));
    }

    return getFallbackFaqItems(navigate, contacts.helpEmail, companyName);
  }, [contacts.helpEmail, navigate, companyName]);
};
